import React from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import axiosInstance from "../../../helpers/axiosConfig";
import { apiUrl, collections, file } from "../../../static/api";
import { normalizeCollectionFiles } from "../../../helpers/normalizeCollectionFiles";
import { FoldersSelector, UserInfo } from "../../../store/selectors";
import { getFoldersImages, SetFolder } from "../../../store/slices/foldersSlice";
// fullScreen import removed — must be called from user gesture only

/**
 * Custom hook that encapsulates all public / private data-fetching logic
 * for the Player component.
 *
 * @param {object}  params    - Route params from useParams()
 * @param {boolean} isPublic  - true when viewing a public shared link
 * @param {boolean} isPrivate - true when viewing a private (logged-in) player
 */
export const usePlayerData = (params, isPublic, isPrivate) => {
  const dispatch = useDispatch();
  const { folderImages, folderItem } = useSelector(FoldersSelector);
  const { userInfo } = useSelector(UserInfo);

  const [state, setState] = React.useState([]);
  const [catalogData, setCatalogData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [isPrivateError, setIsPrivateError] = React.useState(false);
  const [play, setPlay] = React.useState(false);

  // ------------------------------------------------------------------
  // Public data fetching
  // ------------------------------------------------------------------
  const fetchPublicData = React.useCallback(async () => {
    setLoading(true);
    setIsPrivateError(false);
    setError(false);

    // Validate that userId and folderId are numeric
    const userIdNum = parseInt(params.userId, 10);
    const folderIdNum = parseInt(params.folderId, 10);

    if (isNaN(userIdNum) || isNaN(folderIdNum)) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "Invalid route parameters - userId and folderId must be numbers"
        );
      }
      setError(true);
      setIsPrivateError(false);
      setLoading(false);
      return;
    }

    try {
      const [catalogResponse, filesResponse] = await Promise.all([
        axios.get(
          `${apiUrl}${collections}/public/${userIdNum}/${folderIdNum}`
        ),
        axios.get(`${apiUrl}${file}/${userIdNum}/${folderIdNum}`),
      ]);

      const normalized = normalizeCollectionFiles(
        catalogResponse.data,
        filesResponse.data ?? []
      );

      if (process.env.NODE_ENV === "development") {
        const templates = normalized.map((f) => ({ id: f.id, template: f.template }));
        console.log("[Player] Public normalized templates:", templates);
      }

      setCatalogData(catalogResponse.data);
      setState(normalized);
      setError(false);
      setIsPrivateError(false);
    } catch (err) {
      // Error logging - keep minimal for production
      const errorData = err.response?.data || {};
      const statusCode = err.response?.status || errorData.statusCode;
      const errorMessage =
        errorData.message || errorData.error || err.message || "";
      const errorCode = errorData.error || errorData.code || "";
      const errorString = JSON.stringify(errorData).toLowerCase();

      // Enhanced error logging for debugging (development only)
      if (process.env.NODE_ENV === "development") {
        console.log("=== Public Collection Access Error ===");
        console.log(
          "API URL:",
          `${apiUrl}${collections}/${userIdNum}/${folderIdNum}`
        );
        console.log("Error:", err);
        console.log("Error response:", err.response);
        console.log("Error status:", statusCode);
        console.log("Error data:", errorData);
        console.log("Parsed error details:", {
          statusCode,
          errorMessage,
          errorCode,
          errorString: errorString.substring(0, 200),
        });
      }

      // Check for private compilation error indicators
      const isPrivateCompilationError =
        statusCode === 403 ||
        statusCode === 401 ||
        errorCode === "COLLECTION_IS_PRIVATE" ||
        errorMessage.toLowerCase().includes("private") ||
        errorMessage.toLowerCase().includes("collection_is_private") ||
        errorString.includes("private") ||
        errorString.includes("collection_is_private");

      if (isPrivateCompilationError) {
        if (process.env.NODE_ENV === "development") {
          console.log(
            "\u2713 Detected private compilation error - showing PrivateErrorModal"
          );
        }
        setIsPrivateError(true);
        setError(false);
      } else {
        if (process.env.NODE_ENV === "development") {
          console.log(
            "\u2717 Not a private compilation error - showing generic ErrorModal"
          );
        }
        setError(true);
        setIsPrivateError(false);
      }
      setState([]);
    } finally {
      setLoading(false);
    }
  }, [params.folderId, params.userId]);

  // ------------------------------------------------------------------
  // Private: load folder metadata + images
  // ------------------------------------------------------------------
  React.useEffect(() => {
    if (isPrivate && params.folder && userInfo?.id) {
      const folderId = parseInt(params.folder, 10);
      if (!folderItem || folderItem.id !== folderId) {
        // Load folder metadata
        axiosInstance
          .get(`${collections}/${params.folder}`, { timeout: 30000 })
          .then((res) => {
            dispatch(SetFolder(res.data));
          })
          .catch((err) => {
            console.error("Error loading folder:", err);
            if (process.env.NODE_ENV === "development") {
              console.log(
                "Private folder error status:",
                err.response?.status
              );
            }
            if (
              err.response?.status === 403 ||
              err.response?.status === 401
            ) {
              setIsPrivateError(true);
              setError(false);
            } else {
              setError(true);
              setIsPrivateError(false);
            }
          });
        // Load folder images only if we don't already have them
        if (!folderImages.length || folderItem?.id !== folderId) {
          dispatch(
            getFoldersImages({ userId: userInfo.id, id: params.folder })
          );
        }
      }
    }
  }, [
    isPrivate,
    params.folder,
    userInfo?.id,
    dispatch,
    folderItem,
    folderImages.length,
  ]);

  // ------------------------------------------------------------------
  // Private: sync Redux folderImages -> local state
  // ------------------------------------------------------------------
  React.useEffect(() => {
    if (isPrivate && folderImages.length > 0 && folderItem) {
      const normalized = normalizeCollectionFiles(folderItem, folderImages);
      if (process.env.NODE_ENV === "development") {
        const templates = normalized.map((f) => ({ id: f.id, template: f.template }));
        console.log("[Player] Private normalized templates:", templates);
      }
      setState(normalized);
      setCatalogData(folderItem);
      setError(false);
      // Auto-start play when coming from dashboard (private player)
      if (!play) {
        setPlay(true);
        // Note: fullscreen must be requested from a direct user gesture (click/tap).
        // Navigation does not qualify, so we skip it here and let the user
        // trigger fullscreen manually via the player controls.
      }
    }
  }, [isPrivate, folderImages, folderItem, play]);

  // ------------------------------------------------------------------
  // Public: trigger fetch
  // ------------------------------------------------------------------
  React.useEffect(() => {
    if (isPublic) {
      fetchPublicData();
    }
  }, [isPublic, fetchPublicData]);

  return {
    state,
    catalogData,
    loading,
    error,
    isPrivateError,
    play,
    setPlay,
    folderItem,
  };
};
